import type { Color } from "../../common/color.js";
import type { Control } from "../core/Control.js";
import type { Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme, VisualState } from "../themes/Theme.js";

/** Everything an effect is handed for one frame. */
export interface FlareContext {
  readonly rc: RenderContext;
  readonly theme: Theme;
  readonly control: Control;
  /** The control's own bounds, origin at 0,0 — the space it paints in. */
  readonly bounds: Rect;
  readonly state: VisualState;
  /** Seconds since this effect was attached. */
  readonly time: number;
  /** Seconds since the previous frame; 0 on the first. */
  readonly delta: number;
}

/**
 * A decorative effect layered onto a control: an outline, a shadow, a glow, a
 * gradient wash, sparkles.
 *
 * Effects paint around the control rather than inside it, so a control never
 * has to know which ones it is wearing. `paintBelow` runs before the control
 * draws itself and `paintAbove` after, which is the whole difference between
 * a shadow and an outline.
 *
 * Anything that changes on its own reports `animated`, and the control keeps
 * itself registered as a desktop animator for as long as that holds.
 */
export abstract class Flare {
  /** Turn the effect off without detaching it. */
  enabled = true;

  /** Painted beneath the control: shadows, glows, gradient fills. */
  paintBelow(_ctx: FlareContext): void {}

  /** Painted over the control: outlines, sparkles, sheen. */
  paintAbove(_ctx: FlareContext): void {}

  /**
   * True while the effect needs a frame every tick. Returning false when an
   * animation settles lets the desktop go idle again.
   */
  get animated(): boolean {
    return false;
  }

  /**
   * How far outside the control this effect paints, in px.
   *
   * Used for culling — a control whose own bounds are off-screen but whose
   * glow is not must still be painted — and to size the room a container
   * leaves when `clipChildren` is off.
   */
  get overflow(): number {
    return 0;
  }

  /** Called once per frame before painting, for effects that carry state. */
  update(_ctx: FlareContext): void {}

  /** Called when the flare is attached to, or removed from, a control. */
  onAttach(_control: Control): void {}
  onDetach(_control: Control): void {}
}

/**
 * A flare described as data.
 *
 * `kind` selects a factory from the registry, so effects can be stored in
 * config, sent over the wire, or listed in a theme — and new kinds can be
 * registered by an application without touching the framework.
 */
export interface FlareSpec {
  kind: string;
  [option: string]: unknown;
}

export type FlareFactory = (spec: FlareSpec) => Flare;

const registry = new Map<string, FlareFactory>();

/**
 * Register a flare kind. Re-registering a name replaces it, which is how an
 * application overrides a built-in effect with its own.
 */
export function registerFlare(kind: string, factory: FlareFactory): void {
  registry.set(kind, factory);
}

export function flareKinds(): string[] {
  return [...registry.keys()].sort();
}

/** Build a flare from a spec. Throws on an unregistered kind. */
export function createFlare(spec: FlareSpec): Flare {
  const factory = registry.get(spec.kind);
  if (!factory) {
    throw new Error(
      `webwin: unknown flare "${spec.kind}". Registered kinds: ${flareKinds().join(", ") || "none"}`,
    );
  }
  return factory(spec);
}

// --- option readers -----------------------------------------------------
// Specs arrive untyped — from JSON, from a config file — so each factory
// pulls what it needs through these rather than trusting the shape.

export function num(spec: FlareSpec, key: string, fallback: number): number {
  const v = spec[key];
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

export function bool(spec: FlareSpec, key: string, fallback: boolean): boolean {
  const v = spec[key];
  return typeof v === "boolean" ? v : fallback;
}

export function str<T extends string>(spec: FlareSpec, key: string, fallback: T): T {
  const v = spec[key];
  return typeof v === "string" ? (v as T) : fallback;
}

/**
 * Read one of a fixed set of names.
 *
 * Unlike `str`, a value that is a string but not one of the allowed names
 * falls back — which is the difference between an effect that ignores a typo
 * in a config file and one that paints nothing because of it.
 */
export function choice<T extends string>(
  spec: FlareSpec,
  key: string,
  allowed: readonly T[],
  fallback: T,
): T {
  const v = spec[key];
  return typeof v === "string" && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
}

export function color(spec: FlareSpec, key: string): Color | null {
  const v = spec[key];
  if (!v || typeof v !== "object") return null;
  const c = v as Partial<Color>;
  return typeof c.r === "number" && typeof c.g === "number" && typeof c.b === "number"
    ? { r: c.r, g: c.g, b: c.b, a: typeof c.a === "number" ? c.a : 1 }
    : null;
}
